import React from "react";
import { Box, Typography, Grid, useTheme, useMediaQuery, Tooltip } from "@mui/material";
import { LoanDetailsData } from "../../interfaces/common";
import { Skeleton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

interface SummaryLoanDetailsProps {
  data: LoanDetailsData;
  isLoading: boolean;
}

const SummaryLoanDetails: React.FC<SummaryLoanDetailsProps> = ({
  data,
  isLoading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getRepaymentLabel = (type: string) => {
    let val= typeof type === "string"  ? type?.toUpperCase():type;
    switch (val) {
      case "PINT":
        return "Partial interest";
      case "SINT":
        return "Simple interest";
      case "EMI":
        return "EMI";
      default:
        return type || "-";
    }
  };

  // Fixed helper to return specific content or null
  const getTooltipContent = (type: string) => {
    let val= typeof type === "string" ?type?.toUpperCase():type;
    switch (val) {
      case "PINT":
        return (
          <Typography variant="caption">
            <b>Partial Interest:</b> Pay a portion of the interest now; the rest is added to your principal for the next stage of your loan.
          </Typography>
        );
      case "SINT":
        return (
          <Typography variant="caption">
            <b>Simple Interest:</b> Pay full interest now; your principal repayment begins after the moratorium.
          </Typography>
        );
      default:
        return null;
    }
  };
  const activeTooltip = getTooltipContent(data?.Repayment_Type);
  const [isMobileTooltipOpen, setIsMobileTooltipOpen] = React.useState(false);

  if (isLoading) {
    // Mobile skeleton loading
    if (isMobile) {
      return (
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "8px",
            background: "#f8f9fa",
            border: "1px solid #e9ecef",
          }}
        >
          {/* Heading skeleton */}
          <Skeleton variant="text" width="100%" height={20} sx={{ mb: 3 }} />

          {/* Loan Details List skeleton */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {/* Max Loan Amount skeleton */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <Skeleton variant="text" width="40%" height={16} />
              <Skeleton variant="text" width="25%" height={16} />
            </Box>

            {/* Max Tenure skeleton */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <Skeleton variant="text" width="35%" height={16} />
              <Skeleton variant="text" width="20%" height={16} />
            </Box>

            {/* Currency skeleton */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <Skeleton variant="text" width="30%" height={16} />
              <Skeleton variant="text" width="15%" height={16} />
            </Box>

            {/* Repayment Mode skeleton */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
              }}
            >
              <Skeleton variant="text" width="45%" height={16} />
              <Skeleton variant="text" width="30%" height={16} />
            </Box>
          </Box>
        </Box>
      );
    }

    // Desktop skeleton loading
    return (
      <Box sx={{ p: 3, mb: 3, borderRadius: "8px", background: "#f1f1f2" }}>
        {/* Heading skeleton */}
        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />

        {/* Grid skeleton */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="text" width="40%" height={16} />
              <Skeleton variant="text" width="25%" height={16} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="text" width="35%" height={16} />
              <Skeleton variant="text" width="20%" height={16} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="text" width="30%" height={16} />
              <Skeleton variant="text" width="15%" height={16} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="text" width="45%" height={16} />
              <Skeleton variant="text" width="30%" height={16} />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Mobile view that matches the image exactly
  if (isMobile) {
    return (
      <Box
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "8px",
          background: "#f8f9fa",
          border: "1px solid #e9ecef",
        }}
      >
        {/* Heading */}
        <Typography
          sx={{
            mb: 3,
            fontSize: "15px",
            fontWeight: 600,
            color: "#495057",
            fontFamily: "Jost, sans-serif",
            lineHeight: "1.4",
            textAlign: "left",
          }}
        >
          A quick glance at your loan offer details:
        </Typography>

        {/* Loan Details List */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Max Loan Amount */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6c757d",
                fontFamily: "Jost, sans-serif",
              }}
            >
              Max Loan Amount
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#495057",
                fontFamily: "Jost, sans-serif",
              }}
            >
              {data?.Max_Loan_Amount 
                ? `₹ ${new Intl.NumberFormat('en-IN').format(Number(data.Max_Loan_Amount))}` 
                : "₹ 0"
              }
            </Typography>
          </Box>

          {/* Currency */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6c757d",
                fontFamily: "Jost, sans-serif",
              }}
            >
              Currency
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#495057",
                fontFamily: "Jost, sans-serif",
              }}
            >
              {data?.currency || "Rupees (₹)"}
            </Typography>
          </Box>

          {/* Max Tenure */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6c757d",
                fontFamily: "Jost, sans-serif",
              }}
            >
              Max Tenure
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#495057",
                fontFamily: "Jost, sans-serif",
              }}
            >
              {data?.Max_Tenure ? `${data?.Max_Tenure} Months` : "-"}
            </Typography>
          </Box>

          {/* Repayment Mode */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6c757d",
                fontFamily: "Jost, sans-serif",
              }}
            >
              Repayment Mode
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* {true && (
                <Tooltip title={<Box sx={{ p: 0.5 }}>{activeTooltip}</Box>} arrow placement="top">
                  <InfoOutlined sx={{ fontSize: "16px", cursor: "pointer", color: "action.active" }} />
                </Tooltip>
              )} */}
              {activeTooltip && (
                <Tooltip
                  title={<Box sx={{ p: 0.5 }}>{activeTooltip}</Box>}
                  arrow
                  placement="top"
                  open={isMobile ? isMobileTooltipOpen : undefined}
                  onClose={() => isMobile && setIsMobileTooltipOpen(false)}
                  onOpen={() => isMobile && setIsMobileTooltipOpen(true)}
                >
                  <InfoOutlined
                    aria-label="Repayment mode information"
                    onClick={() =>
                      isMobile && setIsMobileTooltipOpen((isOpen) => !isOpen)
                    }
                    sx={{ fontSize: "16px", cursor: "pointer", color: "action.active" }}
                  />
                </Tooltip>
              )}
              <Typography 
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#495057",
                  fontFamily: "Jost, sans-serif",
                  textTransform: 'capitalize'
                }}
              >
                {getRepaymentLabel(data?.Repayment_Type)}
              </Typography>
            </Box>
          </Box>

          {/* ROI */}
          {/* <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#6c757d",
                fontFamily: "Jost, sans-serif",
              }}
            >
              ROI
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#495057",
                fontFamily: "Jost, sans-serif",
              }}
            >
              {data?.Max_Roi ? `${parseFloat(data?.Max_Roi).toFixed(2)}%` : "-"}
            </Typography>
          </Box> */}
        </Box>
      </Box>
    );
  }

  // Desktop view - keep existing unchanged
  return (
    <Box sx={{ p: 3, mb: 3, borderRadius: "8px", background: "#f1f1f2" }}>
      <Typography
        className="text5lable"
        sx={{ mb: 2, fontSize: { xs: "11", sm: "13px" } }}
      >
        A quick glance at your loan offer details:
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography className="text-lable">Max Loan Amount:</Typography>
            <Typography className="value-lable">
              {data?.Max_Loan_Amount 
                ? `₹ ${new Intl.NumberFormat('en-IN').format(Number(data.Max_Loan_Amount))}` 
                : "₹ 0"}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography className="text-lable">Currency:</Typography>
            <Typography className="value-lable">
              {data?.currency || "Rupees (₹)"}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography className="text-lable">Max Tenure:</Typography>
            <Typography className="value-lable">
              {data?.Max_Tenure ? `${data?.Max_Tenure} Months` : "-"}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography className="text-lable">Repayment Mode:</Typography>
            <Typography className="value-lable" sx={{ textTransform: 'capitalize' }}>
              {getRepaymentLabel(data?.Repayment_Type)}
            </Typography>
            
            {activeTooltip && (
              <Tooltip title={<Box sx={{ p: 0.5 }}>{activeTooltip}</Box>} arrow placement="top">
                <InfoOutlined sx={{ fontSize: "16px", cursor: "pointer", color: "action.active" }} />
              </Tooltip>
            )}
          </Box>
        </Grid>
        {/* <Grid item xs={12} sm={6} md={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography className="text-lable">ROI:</Typography>
            <Typography className="value-lable">
              {data?.Max_Roi ? `${parseFloat(data?.Max_Roi).toFixed(2)}%` : "-"}
            </Typography>
          </Box>
        </Grid> */}
      </Grid>
    </Box>
  );
};

export default SummaryLoanDetails;
